import React, { useMemo, useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import snakeToTitleCase from "../utils/SnakeToTitleCase";
import BannerInput from "./BannerInput";
import CustomSpinner from "../utils/CustomSpinner.jsx";
import PolicyPreview from "./PolicyPreview.jsx";

const PolicyAnnouncementFormModal = ({
  manufacturers = [],
  policyTypes = [],
  activeAnnouncement = {},
  displayAnnouncement = {},
  action,
  readOnly = false,
  confirmationMessage,
  onCloseModal,
}) => {
  const initial = useMemo(() => {
    return {
      manufacturer: displayAnnouncement.manufacturer ?? "",
      policyType: displayAnnouncement.policy_type ?? "",
      documentationUrl: displayAnnouncement.documentation_url ?? "",
      targetLaunchDate: displayAnnouncement.target_launch_date_raw ?? "",
      banner: displayAnnouncement.banner ?? "",
      bannerExpiresAt: displayAnnouncement.banner_expires_at_raw ?? "",
      id: displayAnnouncement.id ?? "",
      vanityDate: displayAnnouncement.vanity_date ?? "",
      existingFile: displayAnnouncement.pdf ?? null,
    };
  }, [displayAnnouncement]);

  const [mfr, setMfr] = useState(initial.manufacturer);
  const [policyType, setPolicyType] = useState(initial.policyType);
  const [documentationUrl, setDocumentationUrl] = useState(initial.documentationUrl);
  const [targetLaunchDate, setTargetLaunchDate] = useState(initial.targetLaunchDate);
  const [bannerExpiresAt, setBannerExpiresAt] = useState(initial.bannerExpiresAt);
  const [vanityDate, setVanityDate] = useState(initial.vanityDate);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [banner, setBanner] = useState(initial.banner);
  const STANDARD_BANNER_MSG =
    mfr + " has provided notice of an update to its 340B Contract Pharmacy policy.";
  const [bannerType, setBannerType] = useState(() =>
    displayAnnouncement.banner
      ? displayAnnouncement.banner === STANDARD_BANNER_MSG
        ? "standard"
        : "custom"
      : "none"
  );
  const [formAction, setFormAction] = useState(action);
  const [file, setFile] = useState(null);
  const hasExistingFile = !!initial.existingFile;
  const [isBusy, setIsBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const togglePdfPreview = () => {
    setShowPdfPreview(!showPdfPreview);
  };

  useEffect(() => {
    switch (bannerType) {
      case "standard":
        setBanner(STANDARD_BANNER_MSG);
        break;
      case "custom":
        setBanner("");
        break;
      default:
        setBannerExpiresAt("");
        setBanner("");
    }
  }, [bannerType, setBanner]);

  useEffect(() => {
    switch (bannerType) {
      case "standard":
        setBanner(STANDARD_BANNER_MSG);
        break;
      case "custom":
        setBanner(banner);
    }
  }, [mfr]);

  const isFullFormValid =
    mfr &&
    vanityDate &&
    targetLaunchDate &&
    policyType &&
    documentationUrl &&
    (file || hasExistingFile);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];

    const isPdf = f && (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));

    if (!isPdf) {
      setFile(null);
      setPdfError(
        "The file must not contain any other extensions other than .pdf. Please re-upload the file with the correct naming convention."
      );
      e.target.value = "";
      return;
    }

    setPdfError("");
    setFile(f);
  };

  const buildUrlAndMethod = () => {
    const id = activeAnnouncement.id;
    switch (formAction) {
      case "create":
        return { url: "/policy_announcements", method: "POST" };
      case "update":
        return { url: `/policy_announcements/${id}`, method: "PUT" };
      case "submit":
        return { url: `/policy_announcements/${id}/submit`, method: "POST" };
      case "approve":
        return { url: `/policy_announcements/${id}/approve`, method: "POST" };
      case "reject":
        return { url: `/policy_announcements/${id}/reject`, method: "POST" };
      case "publish":
        return { url: `/policy_announcements/${id}/publish`, method: "POST" };
      case "archive":
        return { url: `/policy_announcements/${id}/archive`, method: "POST" };
      case "revert":
        return { url: `/policy_announcements/${id}/revert`, method: "POST" };
      default:
        throw new Error(`Unknown formAction: ${formAction}`);
    }
  };

  function buildFormData() {
    const formData = new FormData();

    if (action === "revert") {
      formData.append("previous_policy_announcement_id", displayAnnouncement.id);

      return formData;
    }

    formData.append("manufacturer_name", mfr);
    formData.append("policy_type", policyType);
    formData.append("documentation_url", documentationUrl);
    formData.append("target_launch_date", targetLaunchDate);
    formData.append("banner", banner);
    formData.append("banner_expires_at", bannerExpiresAt);
    formData.append("vanity_date", vanityDate);
    file && formData.append("policy_letter", file);
    displayAnnouncement.id && formData.append("id", displayAnnouncement.id);
    formAction === "revert" && formData.append("active_announcement_id", activeAnnouncement.id);

    return formData;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!isFullFormValid) {
      alert("The policy announcement form is missing some required fields.");
      return;
    }

    if (isBusy) return;

    setIsBusy(true);

    try {
      const { url, method } = buildUrlAndMethod();
      const formData = buildFormData();

      const response = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
        },
        body: formData,
      });

      if (response.ok) {
        location.reload();
        return;
      } else {
        console.error("Request failed", response.status);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsBusy(false);
    }
  }

  const renderActionButtons = () => {
    if (action === "create") {
      return (
        <Button type="submit" disabled={!isFullFormValid || isBusy} onClick={handleSubmit}>
          {isBusy ? <CustomSpinner /> : "Save Draft"}
        </Button>
      );
    } else if (action === "update") {
      return (
        <Button type="submit" disabled={!isFullFormValid || isBusy} onClick={handleSubmit}>
          {isBusy ? <CustomSpinner /> : "Save Changes"}
        </Button>
      );
    } else if (action === "review") {
      return (
        <>
          <Button
            type="submit"
            className="btn btn-success"
            disabled={isBusy}
            onClick={() => setFormAction("approve")}
          >
            {isBusy && formAction === "approve" ? <CustomSpinner /> : "Approve"}
          </Button>
          <Button
            type="submit"
            className="btn btn-danger me-3 px-3"
            disabled={isBusy}
            onClick={() => setFormAction("reject")}
          >
            {isBusy && formAction === "reject" ? <CustomSpinner /> : "Reject"}
          </Button>
        </>
      );
    } else if (action === "submit") {
      return (
        <Button type="submit" className="btn btn-success" disabled={isBusy} onClick={handleSubmit}>
          {isBusy ? <CustomSpinner /> : "Submit for Review"}
        </Button>
      );
    } else if (action === "publish") {
      return (
        <>
          <Button
            type="submit"
            className="btn btn-success me-1"
            disabled={isBusy}
            onClick={handleSubmit}
          >
            {isBusy ? <CustomSpinner /> : "Publish"}
          </Button>
          <Button className="btn btn-danger" disabled={isBusy} onClick={onCloseModal}>
            Cancel
          </Button>
        </>
      );
    } else if (action === "archive") {
      return (
        <Button
          type="submit"
          className="btn btn-danger px-3"
          disabled={isBusy}
          onClick={handleSubmit}
        >
          {isBusy ? <CustomSpinner /> : "Archive"}
        </Button>
      );
    } else if (action === "revert") {
      return (
        <>
          <Button
            type="submit"
            className="btn btn-success me-3 px-3"
            disabled={isBusy}
            onClick={() => setFormAction("revert")}
          >
            {isBusy && formAction === "revert" ? <CustomSpinner /> : "Confirm"}
          </Button>
          <Button className="btn btn-danger" disabled={isBusy} onClick={onCloseModal}>
            Cancel
          </Button>
        </>
      );
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit} id="policyAnnouncementForm">
        <Form.Group className="mb-3" controlId="manufacturer_name">
          <Form.Label>Manufacturer</Form.Label>
          <Form.Select
            required
            disabled={readOnly}
            defaultValue={mfr || ""}
            className="select-lg"
            name="policy_announcement[manufacturer_name]"
            onChange={(e) => {
              setMfr(e.target.value);

              if (bannerType === "custom") setBanner("");
            }}
          >
            <option value="">{"< Make a Selection >"}</option>
            {manufacturers.map((manufacturer, i) => (
              <option key={i} value={manufacturer.name}>
                {snakeToTitleCase(manufacturer.name)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3" controlId="policy_type">
          <Form.Label>Policy Type</Form.Label>
          <Form.Select
            required
            disabled={readOnly}
            defaultValue={policyType || ""}
            className="select-lg"
            name="policy_announcement[policy_type]"
            onChange={(e) => setPolicyType(e.target.value)}
          >
            <option value="">Select Policy Type</option>
            {policyTypes.map((type, i) => (
              <option key={i} value={type}>
                {snakeToTitleCase(type)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3" controlId="documentation_url">
          <Form.Label>PS ticket</Form.Label>
          <Form.Control
            required
            type="text"
            disabled={readOnly}
            name="policy_announcement[documentation_url]"
            placeholder="Enter PS ticket link"
            defaultValue={documentationUrl || ""}
            onChange={(e) => setDocumentationUrl(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="pdf">
          {!readOnly && (
            <>
              <Form.Label>Upload PDF</Form.Label>
              <Form.Control
                required={!displayAnnouncement.pdf}
                disabled={readOnly}
                name="policy_announcement[policy_letter]"
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
              />
              {pdfError && (
                <div className="text-danger">
                  <small>{pdfError}</small>
                </div>
              )}
              <small className="text-muted d-block mt-1">
                Current file: {displayAnnouncement.pdf}. Uploading a new file will replace it.
              </small>
            </>
          )}
          {hasExistingFile && (
            <>
              <Form.Check
                type="switch"
                id="custom-switch"
                label="Show PDF"
                onChange={togglePdfPreview}
              />
              {showPdfPreview && <PolicyPreview file={displayAnnouncement.policy_letter_url} />}
              {displayAnnouncement?.pdf?.id && (
                <input
                  type="hidden"
                  name="policy_announcement[existing_policy_letter_id]"
                  value={displayAnnouncement.pdf.id}
                />
              )}
            </>
          )}
        </Form.Group>

        <Form.Group className="mb-3" controlId="target_launch_date">
          <Form.Label>Target Announcement Date</Form.Label>
          <Form.Control
            required
            disabled={readOnly}
            type="date"
            name="policy_announcement[target_launch_date]"
            defaultValue={targetLaunchDate || ""}
            onChange={(e) => setTargetLaunchDate(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="banner">
          <Form.Label>
            Banner <small className="text-muted">(optional)</small>
          </Form.Label>
          <BannerInput
            radioReadOnly={readOnly}
            inputReadOnly={readOnly || bannerType == "standard"}
            banner={banner}
            setBanner={setBanner}
            bannerType={bannerType}
            setBannerType={setBannerType}
          />
        </Form.Group>

        {bannerType != "none" && (
          <Form.Group className="mb-3" controlId="banner_expires_at">
            <Form.Label>Banner Expiration</Form.Label>
            <Form.Control
              type="date"
              disabled={readOnly}
              name="policy_announcement[banner_expires_at]"
              defaultValue={bannerExpiresAt || ""}
              min={new Date().toLocaleDateString("en-CA")} // Form.Control requires the date to be in "YYYY-MM-DD" formatting
              onChange={(e) => setBannerExpiresAt(e.target.value)}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3" controlId="vanity_date">
          <Form.Label>Resources Page Date</Form.Label>
          <Form.Control
            required
            type="date"
            disabled={readOnly}
            name="policy_announcement[vanity_date]"
            defaultValue={vanityDate || ""}
            onChange={(e) => setVanityDate(e.target.value)}
          />
        </Form.Group>
        {confirmationMessage && <p>{confirmationMessage}</p>}
        <Form.Group>{renderActionButtons()}</Form.Group>
      </Form>
    </>
  );
};

export default PolicyAnnouncementFormModal;
